import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api, formatApiError, formatCDF } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { connectSocket, disconnectSocket } from "../../lib/socket";
import { startLiveKitAudioCall, stopLiveKitAudioCall } from "../../lib/livekitCall";
import MapView from "../../components/MapView";
import SOSButton from "../../components/SOSButton";
import RatingModal from "../../components/RatingModal";
import AiInsightCard from "../../components/AiInsightCard";
import { Button, StatusBadge, Badge, Input } from "../../components/ui-kit";
import { ArrowLeft, ChatCircleText, Package, PaperPlaneTilt, Phone, ShieldCheck, Star } from "@phosphor-icons/react";

const STEP_ORDER = [
  "SCHEDULED",
  "SEARCHING_DRIVER",
  "DRIVER_EN_ROUTE",
  "DRIVER_ARRIVED",
  "IN_PROGRESS",
  "RIDE_COMPLETED",
];

export default function RideTracking() {
  const { id } = useParams();
  const { user } = useAuth();
  const [ride, setRide] = useState(null);
  const [paying, setPaying] = useState(false);
  const [autoPaying, setAutoPaying] = useState(false);
  const [paymentRef, setPaymentRef] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);
  const [myRating, setMyRating] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chatText, setChatText] = useState("");
  const [calling, setCalling] = useState(false);
  const [liveCall, setLiveCall] = useState(false);
  const navigate = useNavigate();

  const fetchRide = useCallback(async () => {
    try {
      const { data } = await api.get(`/rides/${id}`);
      setRide(data);
    } catch (e) { /* silent */ }
  }, [id]);

  useEffect(() => {
    fetchRide();
    const int = setInterval(fetchRide, 3000);
    // Realtime
    let cleanup = null;
    (async () => {
      const socket = await connectSocket();
      if (!socket) return;
      socket.connect();
      const onRide = (r) => { if (r?.id === id) setRide(r); };
      const onMessage = (message) => {
        if (message?.ride_id !== id) return;
        setMessages((current) => current.some((m) => m.id === message.id) ? current : [...current, message]);
      };
      const onLoc = (d) => {
        if (d?.ride_id !== id) return;
        setRide((prev) => prev ? { ...prev, driver_location: { lat: d.lat, lng: d.lng } } : prev);
      };
      socket.on("ride.updated", onRide);
      socket.on("ride.message", onMessage);
      socket.on("driver.location", onLoc);
      cleanup = () => {
        socket.off("ride.updated", onRide);
        socket.off("ride.message", onMessage);
        socket.off("driver.location", onLoc);
      };
    })();
    return () => { clearInterval(int); if (cleanup) cleanup(); };
  }, [id, fetchRide]);

  // Fetch my rating when ride completed
  useEffect(() => {
    if (ride?.status === "RIDE_COMPLETED") {
      api.get(`/rides/${id}/rating`).then(({ data }) => setMyRating(data.mine)).catch(() => {});
    }
  }, [ride?.status, id]);

  useEffect(() => {
    api.get(`/rides/${id}/messages`)
      .then(({ data }) => setMessages(data.messages || []))
      .catch(() => {});
  }, [id]);

  const sendMessage = async () => {
    const text = chatText.trim();
    if (!text) return;
    setChatText("");
    try {
      const { data } = await api.post(`/rides/${id}/messages`, { text });
      setMessages((current) => current.some((m) => m.id === data.id) ? current : [...current, data]);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const callDriver = async () => {
    setCalling(true);
    try {
      const { data } = await api.get(`/rides/${id}/livekit-token`);
      await startLiveKitAudioCall({ url: data.url, token: data.token });
      setLiveCall(true);
      toast.success("Appel dans l'app demarre");
      return;
    } catch {
      try {
        await api.post(`/rides/${id}/calls`, { call_type: "phone", target: "driver" });
      } catch {}
    } finally {
      setCalling(false);
    }
    window.location.href = `tel:${ride.driver_phone}`;
  };

  const hangup = async () => {
    await stopLiveKitAudioCall();
    setLiveCall(false);
    toast.success("Appel termine");
  };

  const cancel = async () => {
    setCancelling(true);
    try {
      await api.post(`/rides/${id}/cancel`);
      toast.success("Course annulée");
      navigate("/app");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setCancelling(false); }
  };

  const pay = async () => {
    setPaying(true);
    try {
      await api.post(`/payments/${id}`, {
        method: ride.payment_method,
        mobile_money_provider: ["mpesa", "airtel", "orange"].includes(ride.payment_method) ? ride.payment_method : null,
        phone: user.phone,
        transaction_reference: paymentRef || null,
      });
      toast.success("Paiement enregistré");
      fetchRide();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setPaying(false); }
  };

  const payAutomatically = async () => {
    if (!["mpesa", "airtel", "orange"].includes(ride?.payment_method)) {
      toast.error("Paiement automatique disponible pour M-Pesa, Airtel ou Orange");
      return;
    }
    const phone = user?.phone && !String(user.phone).startsWith("oauth:") ? user.phone : "";
    if (!phone) {
      toast.error("Ajoutez votre numero de telephone dans le profil avant le paiement automatique");
      return;
    }
    setAutoPaying(true);
    try {
      const { data } = await api.post("/mobile-money/checkout", {
        target_type: "ride",
        target_id: ride.id,
        provider: ride.payment_method,
        phone,
        amount: ride.price,
      });
      if (data?.status === "SUCCEEDED") {
        toast.success("Paiement confirme automatiquement");
      } else {
        toast.message(data?.message || "Paiement en attente du provider");
      }
      fetchRide();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAutoPaying(false);
    }
  };

  if (!ride) {
    return <div className="phone-frame flex items-center justify-center bg-premium-grad" data-testid="ride-loading">
      <div className="font-heading text-xl font-bold">Chargement…</div>
    </div>;
  }

  const currentStepIdx = STEP_ORDER.indexOf(ride.status);
  const isDelivery = ride.service_type === "delivery";
  const title = (isDelivery ? {
    SCHEDULED: "Livraison programmee",
    SEARCHING_DRIVER: "Recherche d'un livreur",
    DRIVER_EN_ROUTE: "Livreur en route",
    DRIVER_ARRIVED: "Livreur au point de recuperation",
    IN_PROGRESS: "Colis en livraison",
    RIDE_COMPLETED: "Livraison terminee",
    CANCELLED: "Livraison annulee",
  } : {
    SCHEDULED: "Course programmee",
    SEARCHING_DRIVER: "Recherche d'un chauffeur",
    DRIVER_EN_ROUTE: "Chauffeur en route",
    DRIVER_ARRIVED: "Chauffeur arrivé",
    IN_PROGRESS: "Course en cours",
    RIDE_COMPLETED: "Course terminée",
    CANCELLED: "Course annulée",
  })[ride.status] || ride.status;
  const directPayment = ["cash", "mpesa", "airtel", "orange"].includes(ride.payment_method);
  const directPaymentLabels = { cash: "Cash", mpesa: "M-Pesa", airtel: "Airtel Money", orange: "Orange Money" };

  return (
    <div className="phone-frame phone-screen-lock relative">
      <MapView
        center={ride.driver_location || ride.pickup}
        pickup={ride.pickup}
        destination={ride.destination}
        extraStop={ride.extra_stop}
        driver={ride.driver_location}
        driverVehicleType={ride.vehicle_type}
        height="100dvh"
        className="phone-map-height"
        showAttribution={false}
      />

      {/* Top */}
      <div className="map-top-controls-safe absolute inset-x-0 z-[800]">
        <div className="map-top-row flex items-center justify-between gap-2">
          <button
            onClick={() => navigate("/app")}
            className="map-top-button rounded-full glass-strong p-3 press-down"
            data-testid="back-btn"
          >
            <ArrowLeft size={18} weight="bold" />
          </button>
          <StatusBadge status={ride.status} />
          {["DRIVER_EN_ROUTE", "DRIVER_ARRIVED", "IN_PROGRESS"].includes(ride.status) && (
            <SOSButton rideId={ride.id} />
          )}
        </div>
      </div>

      <div className="phone-bottom-panel absolute inset-x-0 bottom-0 z-[800] mx-auto px-2">
        <motion.div
          initial={{ y: 200, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="glass-strong rounded-3xl p-4 min-[390px]:p-5"
        >
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
              #{ride.id.slice(0, 6).toUpperCase()} · {isDelivery ? "Livraison" : ride.vehicle_type}
            </div>
            <h2 className="font-heading text-2xl font-bold">{title}</h2>
          </div>

          {/* Progress */}
          <div className="mt-4 flex items-center gap-1">
            {STEP_ORDER.map((s, i) => (
              <div
                key={s}
                className={`h-1.5 flex-1 rounded-full transition-all ${
                  i <= currentStepIdx ? "bg-primary glow-blue" : "bg-white/10"
                }`}
              />
            ))}
          </div>

	          <AiInsightCard
	            endpoint={`/rides/${ride.id}/ai`}
	            title="IA course"
	            limit={2}
	            compact
	            className="mt-3"
	          />

	          {ride.is_for_someone_else && (
	            <div className="mt-3 rounded-2xl border border-primary/25 bg-primary/10 p-3" data-testid="recipient-summary">
	              <div className="text-[10px] font-bold uppercase tracking-widest text-primary">
                  {isDelivery ? "Destinataire livraison" : "Course pour une autre personne"}
                </div>
	              <div className="mt-1 font-heading text-lg font-bold">{ride.recipient_name}</div>
	              <div className="font-mono text-[11px] text-muted-foreground">{ride.recipient_phone}</div>
	              {ride.recipient_note && <div className="mt-2 rounded-xl bg-black/20 p-2 text-xs text-muted-foreground">{ride.recipient_note}</div>}
	            </div>
	          )}

            {isDelivery && ride.delivery && (
              <div className="mt-3 rounded-2xl border border-secondary/25 bg-secondary/10 p-3" data-testid="delivery-summary">
                <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-secondary">
                  <Package size={14} weight="fill" /> Colis
                </div>
                <div className="mt-1 font-bold">{ride.delivery.package_description}</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Taille: {ride.delivery.package_size || "-"}
                </div>
              </div>
            )}

	          {/* Driver info */}
          {ride.driver_name && (
            <div className="mt-4 flex items-center justify-between rounded-2xl bg-white/5 p-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-primary to-secondary font-bold text-primary-foreground">
                  {ride.driver_name[0]}
                </div>
                <div>
                  <div className="font-heading text-base font-bold">{ride.driver_name}</div>
                  <div className="font-mono text-[11px] text-muted-foreground">{ride.driver_phone}</div>
                </div>
              </div>
              {liveCall ? (
                <button onClick={hangup} className="rounded-full bg-destructive/20 px-3 py-2 text-xs font-bold text-destructive" data-testid="hangup-call">
                  Raccrocher
                </button>
              ) : (
                <button onClick={callDriver} disabled={calling} className="rounded-full bg-secondary/15 p-2 text-secondary disabled:opacity-50" data-testid="call-driver">
                  <Phone size={18} weight="fill" />
                </button>
              )}
            </div>
          )}

          {ride.driver_location && ["DRIVER_EN_ROUTE", "DRIVER_ARRIVED", "IN_PROGRESS"].includes(ride.status) && (
            <div className="mt-3 rounded-2xl border border-primary/25 bg-primary/10 p-3 text-xs" data-testid="live-driver-tracking">
              <div className="font-bold text-primary">Suivi live du chauffeur actif</div>
              <div className="text-muted-foreground">
                Le point jaune sur la carte se met a jour automatiquement pendant que le chauffeur vient vers vous.
              </div>
            </div>
          )}

          {liveCall && (
            <div className="mt-3 rounded-2xl border border-secondary/30 bg-secondary/10 p-3 text-xs" data-testid="livekit-call-active">
              <div className="font-bold text-secondary">Appel audio actif dans NEXORA</div>
              <div className="text-muted-foreground">Micro connecte. Le chauffeur peut rejoindre la meme salle de course.</div>
            </div>
          )}

          {ride.driver_name && (
            <div className="mt-3 rounded-2xl bg-white/5 p-3" data-testid="ride-chat">
              <div className="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                <ChatCircleText size={14} weight="bold" /> Chat client-chauffeur
              </div>
              <div className="max-h-28 space-y-2 overflow-y-auto pr-1">
                {messages.length === 0 && (
                  <div className="text-[11px] text-muted-foreground">Aucun message pour le moment</div>
                )}
                {messages.slice(-8).map((message) => {
                  const mine = message.sender_id === user.id;
                  return (
                    <div key={message.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-xs ${mine ? "bg-primary text-primary-foreground" : "bg-white/10 text-foreground"}`}>
                        {message.text}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-2 flex gap-2">
                <input
                  value={chatText}
                  onChange={(e) => setChatText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") sendMessage();
                  }}
                  placeholder="Ecrire au chauffeur..."
                  className="min-w-0 flex-1 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none"
                  data-testid="chat-input"
                />
                <button
                  onClick={sendMessage}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground press-down"
                  data-testid="chat-send"
                >
                  <PaperPlaneTilt size={16} weight="fill" />
                </button>
              </div>
            </div>
          )}

          {(ride.status === "DRIVER_EN_ROUTE" || ride.status === "DRIVER_ARRIVED") && (
            <div className="mt-3 rounded-2xl border border-primary/30 bg-primary/10 p-3">
              <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-primary">
                <ShieldCheck size={14} weight="bold" /> {isDelivery ? "Code a donner au livreur" : "Code à donner au chauffeur"}
              </div>
              <div className="mt-1 font-mono text-4xl font-black tracking-[0.3em] text-primary" data-testid="ride-pin">
                {ride.pin}
              </div>
            </div>
          )}

          <div className="mt-4 grid grid-cols-2 gap-3 border-t border-white/10 pt-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Prix</div>
              <div className="font-mono text-xl font-bold" data-testid="ride-price">{formatCDF(ride.price)}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Paiement</div>
              <div className="text-sm"><Badge tone="primary">{ride.payment_method}</Badge> <Badge tone={ride.payment_status === "COMPLETED" ? "success" : "warning"}>{ride.payment_status}</Badge></div>
            </div>
          </div>

          {ride.status === "RIDE_COMPLETED" && directPayment && ride.payment_status !== "COMPLETED" && (
            <div className="mt-3 rounded-2xl border border-secondary/25 bg-secondary/10 p-3 text-xs">
              <div className="font-bold text-secondary">
                Payez directement {isDelivery ? "le livreur" : "le chauffeur"} par {directPaymentLabels[ride.payment_method] || "Mobile Money"}.
              </div>
              <div className="mt-1 text-muted-foreground">
                Numero {isDelivery ? "livreur" : "chauffeur"}: <span className="font-mono text-foreground">{ride.driver_phone}</span>. Apres l'envoi, indiquez la reference si vous l'avez. {isDelivery ? "Le livreur" : "Le chauffeur"} confirmera la reception.
              </div>
              {ride.payment_status === "WAITING_DRIVER_CONFIRMATION" ? (
                <Badge tone="warning" className="mt-2">Attente confirmation chauffeur</Badge>
              ) : ride.payment_status === "PROVIDER_PENDING" ? (
                <Badge tone="warning" className="mt-2">Attente confirmation provider</Badge>
              ) : (
                <>
                  {ride.payment_method !== "cash" && (
                    <Button
                      variant="secondary"
                      className="mt-3 w-full"
                      onClick={payAutomatically}
                      disabled={autoPaying}
                      data-testid="mobile-money-auto-pay"
                    >
                      {autoPaying ? "Paiement..." : "Payer automatiquement"}
                    </Button>
                  )}
                  <Input
                    className="mt-2 font-mono text-sm"
                    value={paymentRef}
                    onChange={(e) => setPaymentRef(e.target.value)}
                    placeholder="Reference transaction optionnelle"
                    data-testid="payment-reference"
                  />
                </>
              )}
            </div>
          )}

          {(ride.scheduled_at || ride.extra_stop || ride.ride_options?.length > 0) && (
            <div className="mt-3 rounded-2xl bg-white/5 p-3 text-xs" data-testid="ride-options-summary">
              {ride.scheduled_at && (
                <div>
                  <span className="font-bold text-foreground">Programmee:</span>{" "}
                  <span className="text-muted-foreground">{new Date(ride.scheduled_at).toLocaleString("fr-FR")}</span>
                </div>
              )}
              {ride.extra_stop && (
                <div className="mt-1">
                  <span className="font-bold text-foreground">Arret:</span>{" "}
                  <span className="text-muted-foreground">{ride.extra_stop.address || `${ride.extra_stop.lat}, ${ride.extra_stop.lng}`}</span>
                </div>
              )}
              {ride.ride_options?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {ride.ride_options.map((option) => (
                    <Badge key={option} tone="default">{option}</Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="mt-4 flex gap-2">
            {ride.status === "RIDE_COMPLETED" && ride.payment_status !== "COMPLETED" && (
              <Button variant="primary" className="flex-1" onClick={pay} disabled={paying} data-testid="pay-button">
                {paying ? "Paiement..." : directPayment ? "J'ai envoye" : "Payer maintenant"}
              </Button>
            )}
            {ride.status === "RIDE_COMPLETED" && !myRating && (
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setRateOpen(true)}
                data-testid="rate-button"
              >
                <Star weight="fill" /> Noter
              </Button>
            )}
            {["SCHEDULED", "SEARCHING_DRIVER", "DRIVER_EN_ROUTE", "DRIVER_ARRIVED"].includes(ride.status) && (
              <Button variant="danger" className="flex-1" onClick={cancel} disabled={cancelling} data-testid="cancel-ride">
                {cancelling ? "..." : "Annuler"}
              </Button>
            )}
            {(ride.status === "RIDE_COMPLETED" || ride.status === "CANCELLED") && (
              <Link to="/app" className="flex-1" data-testid="back-home">
                <Button variant="primary" className="w-full">Retour accueil</Button>
              </Link>
            )}
          </div>
        </motion.div>
      </div>
      <RatingModal
        open={rateOpen}
        onClose={() => setRateOpen(false)}
        rideId={ride.id}
        targetName={ride.driver_name || ""}
        onDone={() => {
          setRateOpen(false);
          setMyRating({ rating: 5 });
        }}
      />
    </div>
  );
}
